import { service } from '@ember/service';

import type { Service } from 'webapp-types';
import { BillingFeature, DeliveryType } from 'webapp/enums/enums';
import { NotificationFilterType, NotificationScheduleType } from 'webapp/enums/notifications';

import type { ReportScheduleSetting } from 'webapp/models-custom';
import NotificationBase from 'webapp/models-custom/notification/base';

export default class NotificationReportScheduleSetting extends NotificationBase {
	@service('model-manager/report-schedule-settings')
	declare mmReportScheduleSettingsService: Service.ModelManager.ReportScheduleSettings;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;

	scheduleType = NotificationScheduleType.UPDATE;
	targetObject: ReportScheduleSetting;
	type = NotificationFilterType.REPORT;
	typeColor = 'var(--databox-violet-500)';
	typeIcon: IconName = 'Reports';

	override billingFeature = BillingFeature.REPORTS_BASIC;

	constructor(model: ReportScheduleSetting) {
		super();
		this.targetObject = model;
	}

	get title() {
		return this.targetObject.report?.title || '';
	}

	get subtitleText() {
		return this.targetObject.report?.slideCountText || '';
	}

	get isMine() {
		return this.targetObject.isMine;
	}

	override get canPreview() {
		return true;
	}

	get schedule() {
		return this.targetObject.schedule;
	}

	get frequency() {
		const frequency = this.mmNotificationsService.getFrequencyFromSchedule(this.schedule.type);
		return frequency ? [frequency] : [];
	}

	override get frequencySortProp() {
		return this.schedule.deliveryTimeLevel;
	}

	get deliveryTypes() {
		const deliveryTypes: DeliveryType[] = [];
		const reportScheduleSetting = this.targetObject;
		if (reportScheduleSetting.isEmailDelivery) {
			deliveryTypes.push(DeliveryType.EMAIL);
		}
		if (reportScheduleSetting.isSlackDelivery) {
			deliveryTypes.push(DeliveryType.SLACK);
		}
		return deliveryTypes;
	}

	get frequencyTooltipTextTop() {
		const schedule = this.schedule;
		return `${schedule.typeText} ${schedule.timeText}`;
	}

	get createdBy() {
		return this.targetObject.createdByUser;
	}

	get createdOn() {
		return this.targetObject.created;
	}

	get emailRecipients() {
		// cast existing emails into user ids
		const users = this.mmUsersService.activatedUserRecords;
		return this.targetObject.recipients.map((email) => {
			const userId = users.find((u) => u.email === email)?.id;
			return userId ? +userId : email;
		});
	}

	get allRecipients() {
		return this.targetObject.mappedRecipients;
	}

	override preview() {
		const report = this.targetObject.report;
		if (report) {
			this.mmReportsService.openPreviewPopup(report, 'notifications');
		}
	}

	delete() {
		const reportScheduleSetting = this.targetObject;
		if (this.isMine) {
			void reportScheduleSetting.deleteAndCatch();
		} else {
			const email = this.currentUserService.model?.email || '';
			reportScheduleSetting.recipients = reportScheduleSetting.recipients.filter(
				(e) => e !== email,
			);
			void reportScheduleSetting.saveAndCatch();
		}
	}
}
